import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { X, Download, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, subWeeks, addMonths, subMonths } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { Portal } from '../../../components/ui/Portal';
import { useLanguage } from '../../../context/LanguageContext';
import { fetchMeetingsForExport, countMeetingsForExport, fetchInboxForExport, countInboxForExport } from '../../../services/exportService';
import { exportToFile, exportToFileWithInbox, isInboxExportEnabled } from '../../../utils/exportUtils';

type ExportFormat = 'csv' | 'xlsx';
type DateRangeType = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'thisYear' | 'allTime' | 'custom';
type TabType = 'periods' | 'calendar';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountName: string;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  accountName
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const locale = language === 'es' ? es : enUS;

  // State
  const [exportFormat, setExportFormat] = useState<ExportFormat>('xlsx');
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('thisMonth');
  const [isExporting, setIsExporting] = useState(false);
  const [meetingCount, setMeetingCount] = useState<number | null>(null);
  const [inboxCount, setInboxCount] = useState<number | null>(null);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const [isInboxCountLoading, setIsInboxCountLoading] = useState(false);

  // Check if this account has inbox export enabled
  const hasInboxExport = useMemo(() => isInboxExportEnabled(accountName), [accountName]);

  // Determine if download should be enabled
  const canDownload = useMemo(() => {
    if (isCountLoading || isInboxCountLoading) return false;

    // For inbox-enabled accounts with xlsx, allow if either has data
    if (hasInboxExport && exportFormat === 'xlsx') {
      return (meetingCount !== null && meetingCount > 0) || (inboxCount !== null && inboxCount > 0);
    }

    // For CSV or non-inbox accounts, only meetings matter
    return meetingCount !== null && meetingCount > 0;
  }, [isCountLoading, isInboxCountLoading, hasInboxExport, exportFormat, meetingCount, inboxCount]);

  // Period selector state
  const [activeTab, setActiveTab] = useState<TabType>('periods');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Calendar state for custom range
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

  // Track selected period from Periods tab
  const [selectedPeriodMonth, setSelectedPeriodMonth] = useState<number | null>(null);
  const [selectedPeriodQuarter, setSelectedPeriodQuarter] = useState<number | null>(null);
  const [selectedPeriodYear, setSelectedPeriodYear] = useState<number | null>(null);
  const [selectedPeriodYearValue, setSelectedPeriodYearValue] = useState<number | null>(null);

  const currentYear = new Date().getFullYear();

  // Reset all state when modal opens
  useEffect(() => {
    if (isOpen) {
      setExportFormat('xlsx');
      setDateRangeType('thisMonth');
      setMeetingCount(null);
      setInboxCount(null);
      setIsInboxCountLoading(false);
      setActiveTab('periods');
      setSelectedYear(new Date().getFullYear());
      setCalendarMonth(new Date());
      setCustomStartDate(null);
      setCustomEndDate(null);
      setSelectedPeriodMonth(null);
      setSelectedPeriodQuarter(null);
      setSelectedPeriodYear(null);
      setSelectedPeriodYearValue(null);
    }
  }, [isOpen]);

  // Calculate date range based on selection
  const getDateRange = useCallback((): { start: string; end: string; label: string; startDate: Date; endDate: Date } => {
    const today = new Date();
    let start: Date;
    let end: Date;
    let label: string;

    switch (dateRangeType) {
      case 'thisWeek':
        start = startOfWeek(today, { weekStartsOn: 1 });
        end = endOfWeek(today, { weekStartsOn: 1 });
        label = `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`;
        break;
      case 'lastWeek': {
        const lastWeek = subWeeks(today, 1);
        start = startOfWeek(lastWeek, { weekStartsOn: 1 });
        end = endOfWeek(lastWeek, { weekStartsOn: 1 });
        label = `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`;
        break;
      }
      case 'thisMonth':
        start = startOfMonth(today);
        end = endOfMonth(today);
        label = format(today, 'MMMM yyyy', { locale });
        break;
      case 'thisYear':
        start = startOfYear(today);
        end = endOfYear(today);
        label = today.getFullYear().toString();
        break;
      case 'allTime':
        start = new Date(2023, 0, 1);
        end = today;
        label = t('export.allTime');
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          start = customStartDate;
          end = customEndDate;
          // Check if it's a full month selection
          const isFullMonth = start.getDate() === 1 &&
            end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate() &&
            start.getMonth() === end.getMonth() &&
            start.getFullYear() === end.getFullYear();

          if (isFullMonth) {
            label = format(start, 'MMMM yyyy', { locale });
          } else {
            label = `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`;
          }
        } else {
          start = startOfMonth(today);
          end = endOfMonth(today);
          label = t('export.selectRange');
        }
        break;
      default:
        start = startOfMonth(today);
        end = endOfMonth(today);
        label = format(today, 'MMMM yyyy', { locale });
    }

    return {
      start: format(start, 'yyyy-MM-dd'),
      end: format(end, 'yyyy-MM-dd'),
      label,
      startDate: start,
      endDate: end
    };
  }, [dateRangeType, customStartDate, customEndDate, t, locale]);

  // Fetch meeting count when date range changes
  useEffect(() => {
    const fetchCount = async () => {
      if (!isOpen || !accountName) return;

      // Don't count for custom range until both dates are selected
      if (dateRangeType === 'custom' && (!customStartDate || !customEndDate)) {
        setMeetingCount(null);
        return;
      }

      setIsCountLoading(true);
      try {
        const { startDate, endDate } = getDateRange();
        // "All time" is open-ended so meetings already booked ahead are included.
        const count = await countMeetingsForExport(accountName, startDate, dateRangeType === 'allTime' ? null : endDate);
        setMeetingCount(count);
      } catch (error) {
        console.error('Error fetching meeting count:', error);
        setMeetingCount(null);
      } finally {
        setIsCountLoading(false);
      }
    };

    fetchCount();
  }, [isOpen, accountName, dateRangeType, customStartDate, customEndDate, getDateRange]);

  // Fetch inbox count when modal opens (for inbox-enabled accounts)
  useEffect(() => {
    const fetchInboxCountData = async () => {
      if (!isOpen || !accountName || !hasInboxExport) {
        setInboxCount(null);
        setIsInboxCountLoading(false);
        return;
      }

      setIsInboxCountLoading(true);
      try {
        const count = await countInboxForExport(accountName);
        setInboxCount(count);
      } catch (error) {
        console.error('Error fetching inbox count:', error);
        setInboxCount(null);
      } finally {
        setIsInboxCountLoading(false);
      }
    };

    fetchInboxCountData();
  }, [isOpen, accountName, hasInboxExport]);

  // Handle export
  const handleExport = async () => {
    if (!accountName) return;

    setIsExporting(true);
    try {
      const { start, end, startDate, endDate } = getDateRange();
      const meetings = await fetchMeetingsForExport(accountName, startDate, dateRangeType === 'allTime' ? null : endDate);

      // For inbox-enabled accounts with xlsx format, include inbox data
      if (hasInboxExport && exportFormat === 'xlsx') {
        const inboxData = await fetchInboxForExport(accountName);

        // Allow export if either meetings or inbox has data
        if (meetings.length === 0 && inboxData.length === 0) {
          return;
        }

        exportToFileWithInbox(
          meetings,
          inboxData,
          accountName,
          start,
          end,
          language as 'es' | 'en'
        );
      } else {
        // Regular export (CSV or non-inbox accounts)
        if (meetings.length === 0) {
          return;
        }
        exportToFile(
          meetings,
          accountName,
          start,
          end,
          exportFormat,
          language as 'es' | 'en'
        );
      }

      onClose();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Quick period selection handlers - clear period selections
  const handleQuickSelect = (type: DateRangeType) => {
    setDateRangeType(type);
    setSelectedPeriodMonth(null);
    setSelectedPeriodQuarter(null);
    setSelectedPeriodYear(null);
    setSelectedPeriodYearValue(null);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  // Month selection from periods tab
  const handleMonthClick = (monthIndex: number) => {
    const start = startOfMonth(new Date(selectedYear, monthIndex));
    const end = endOfMonth(new Date(selectedYear, monthIndex));
    setCustomStartDate(start);
    setCustomEndDate(end);
    setDateRangeType('custom');
    // Set selection state
    setSelectedPeriodMonth(monthIndex);
    setSelectedPeriodQuarter(null);
    setSelectedPeriodYear(null);
    setSelectedPeriodYearValue(selectedYear);
  };

  // Quarter selection from periods tab
  const handleQuarterClick = (quarter: number) => {
    const start = startOfQuarter(new Date(selectedYear, (quarter - 1) * 3));
    const end = endOfQuarter(new Date(selectedYear, (quarter - 1) * 3));
    setCustomStartDate(start);
    setCustomEndDate(end);
    setDateRangeType('custom');
    // Set selection state
    setSelectedPeriodMonth(null);
    setSelectedPeriodQuarter(quarter);
    setSelectedPeriodYear(null);
    setSelectedPeriodYearValue(selectedYear);
  };

  // Year selection from periods tab
  const handleYearSelect = () => {
    const start = startOfYear(new Date(selectedYear, 0));
    const end = endOfYear(new Date(selectedYear, 0));
    setCustomStartDate(start);
    setCustomEndDate(end);
    setDateRangeType('custom');
    // Set selection state
    setSelectedPeriodMonth(null);
    setSelectedPeriodQuarter(null);
    setSelectedPeriodYear(selectedYear);
    setSelectedPeriodYearValue(selectedYear);
  };

  // Calendar date selection
  const handleCalendarDateClick = (date: Date) => {
    // Clear period selections when using calendar
    setSelectedPeriodMonth(null);
    setSelectedPeriodQuarter(null);
    setSelectedPeriodYear(null);
    setSelectedPeriodYearValue(null);

    if (!customStartDate || (customStartDate && customEndDate)) {
      // Start new selection
      setCustomStartDate(date);
      setCustomEndDate(null);
      setDateRangeType('custom');
    } else {
      // Complete selection
      if (date < customStartDate) {
        setCustomEndDate(customStartDate);
        setCustomStartDate(date);
      } else {
        setCustomEndDate(date);
      }
      setDateRangeType('custom');
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    for (let i = 0; i < adjustedStart; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  const isDateInRange = (date: Date) => {
    if (!customStartDate || !customEndDate) return false;
    return date >= customStartDate && date <= customEndDate;
  };

  const isDateSelected = (date: Date) => {
    if (!date) return false;
    if (customStartDate && date.getTime() === customStartDate.getTime()) return true;
    if (customEndDate && date.getTime() === customEndDate.getTime()) return true;
    return false;
  };

  // Check if a month is selected in the periods tab
  const isMonthSelected = useMemo(() => (monthIndex: number) => {
    return selectedPeriodMonth === monthIndex && selectedPeriodYearValue === selectedYear;
  }, [selectedPeriodMonth, selectedPeriodYearValue, selectedYear]);

  // Check if a quarter is selected in the periods tab
  const isQuarterSelected = useMemo(() => (quarter: number) => {
    return selectedPeriodQuarter === quarter && selectedPeriodYearValue === selectedYear;
  }, [selectedPeriodQuarter, selectedPeriodYearValue, selectedYear]);

  // Check if year is selected
  const isYearSelected = useMemo(() => {
    return selectedPeriodYear === selectedYear && selectedPeriodYearValue === selectedYear;
  }, [selectedPeriodYear, selectedPeriodYearValue, selectedYear]);

  if (!isOpen) return null;

  const { label: rangeLabel } = getDateRange();
  const monthNames = language === 'es'
    ? ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const dayNames = language === 'es'
    ? ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div className="bg-white shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">{t('export.title')}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-5 overflow-y-auto flex-1">
            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('export.format')}
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setExportFormat('xlsx')}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    exportFormat === 'xlsx'
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Excel (.xlsx)
                </button>
                <button
                  onClick={() => setExportFormat('csv')}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    exportFormat === 'csv'
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  CSV (.csv)
                </button>
              </div>
            </div>

            {/* Date Range Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('export.dateRange')}
              </label>

              {/* Quick Options */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => handleQuickSelect('thisWeek')}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    dateRangeType === 'thisWeek'
                      ? 'bg-[#0055FF] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('export.thisWeek')}
                </button>
                <button
                  onClick={() => handleQuickSelect('lastWeek')}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    dateRangeType === 'lastWeek'
                      ? 'bg-[#0055FF] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('export.lastWeek')}
                </button>
                <button
                  onClick={() => handleQuickSelect('thisMonth')}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    dateRangeType === 'thisMonth'
                      ? 'bg-[#0055FF] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('export.thisMonth')}
                </button>
                <button
                  onClick={() => handleQuickSelect('allTime')}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    dateRangeType === 'allTime'
                      ? 'bg-[#0055FF] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('export.allTime')}
                </button>
              </div>

              {/* Period/Calendar Selector */}
              <div className="border border-gray-200">
                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab('periods')}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'periods'
                        ? 'text-gray-900 border-b-2 border-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('export.periods')}
                  </button>
                  <button
                    onClick={() => setActiveTab('calendar')}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'calendar'
                        ? 'text-gray-900 border-b-2 border-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('export.calendar')}
                  </button>
                </div>

                {/* Tab Content */}
                <div className="p-4">
                  {activeTab === 'periods' ? (
                    <div className="space-y-4">
                      {/* Year Selector */}
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => setSelectedYear(selectedYear - 1)}
                          className="p-1 hover:bg-gray-100"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="font-semibold text-gray-900">{selectedYear}</span>
                        <button
                          onClick={() => setSelectedYear(selectedYear + 1)}
                          disabled={selectedYear >= currentYear}
                          className="p-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Quarters and Months */}
                      <div className="flex gap-3">
                        {/* Quarters Column */}
                        <div className="flex flex-col gap-2">
                          {[1, 2, 3, 4].map(quarter => (
                            <button
                              key={quarter}
                              onClick={() => handleQuarterClick(quarter)}
                              className={`px-3 py-2 text-center text-sm cursor-pointer transition-colors ${
                                isQuarterSelected(quarter)
                                  ? 'bg-[#0055FF] text-white'
                                  : 'hover:bg-gray-100 text-gray-700'
                              }`}
                            >
                              Q{quarter}
                            </button>
                          ))}
                        </div>

                        {/* Months Grid */}
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          {monthNames.map((month, idx) => (
                            <button
                              key={month}
                              onClick={() => handleMonthClick(idx)}
                              className={`px-3 py-2 text-center text-sm cursor-pointer transition-colors ${
                                isMonthSelected(idx)
                                  ? 'bg-[#0055FF] text-white'
                                  : 'hover:bg-gray-100 text-gray-700'
                              }`}
                            >
                              {month}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Year button */}
                      <div className="flex gap-2 pt-3 border-t border-gray-200">
                        <button
                          onClick={handleYearSelect}
                          className={`flex-1 px-3 py-2 text-sm transition-colors ${
                            isYearSelected
                              ? 'bg-[#0055FF] text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {t('export.allYear', { year: selectedYear })}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Calendar Month Navigation */}
                      <div className="flex items-center justify-between mb-4">
                        <button
                          onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                          className="p-1 hover:bg-gray-100"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="font-semibold text-gray-900">
                          {format(calendarMonth, 'MMMM yyyy', { locale })}
                        </span>
                        <button
                          onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                          className="p-1 hover:bg-gray-100"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Calendar Grid */}
                      <div>
                        {/* Day headers */}
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {dayNames.map(day => (
                            <div key={day} className="text-center text-xs font-medium text-gray-600">
                              {day}
                            </div>
                          ))}
                        </div>

                        {/* Calendar days */}
                        <div className="grid grid-cols-7 gap-1">
                          {calendarDays.map((date, idx) => (
                            <div key={idx}>
                              {date ? (
                                <button
                                  onClick={() => handleCalendarDateClick(date)}
                                  className={`w-full aspect-square flex items-center justify-center text-sm transition-colors ${
                                    isDateSelected(date)
                                      ? 'bg-[#0055FF] text-white'
                                      : isDateInRange(date)
                                      ? 'bg-blue-100 text-gray-900'
                                      : 'hover:bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {date.getDate()}
                                </button>
                              ) : (
                                <div className="w-full aspect-square" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Calendar Selection Info */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                        <div className="text-xs text-gray-600">
                          {customStartDate && customEndDate && (
                            <span>
                              {format(customStartDate, 'dd/MM/yyyy')} - {format(customEndDate, 'dd/MM/yyyy')}
                            </span>
                          )}
                          {customStartDate && !customEndDate && (
                            <span>{t('export.selectEndDate')}</span>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setCustomStartDate(null);
                            setCustomEndDate(null);
                            setSelectedPeriodMonth(null);
                            setSelectedPeriodQuarter(null);
                            setSelectedPeriodYear(null);
                            setSelectedPeriodYearValue(null);
                          }}
                          className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
                        >
                          {t('export.clear')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Selected Range Display */}
            <div className="bg-gray-50 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('export.selectedRange')}:</span>
                <span className="text-sm font-medium text-gray-900">{rangeLabel}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm text-gray-600">{t('export.meetingsToExport')}:</span>
                <span className="text-sm font-medium text-gray-900">
                  {isCountLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin inline" />
                  ) : meetingCount !== null ? (
                    meetingCount
                  ) : (
                    '-'
                  )}
                </span>
              </div>
              {/* Show inbox count for enabled accounts */}
              {hasInboxExport && (
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm text-gray-600">{t('export.inboxToExport')}:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {isInboxCountLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin inline" />
                    ) : inboxCount !== null ? (
                      inboxCount
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
              )}
              {/* Note about inbox export being xlsx only */}
              {hasInboxExport && exportFormat === 'csv' && (
                <p className="text-xs text-gray-500 mt-2">
                  {t('export.inboxXlsxOnly')}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-white">
            <button
              onClick={onClose}
              className="text-sm text-gray-700 hover:text-gray-900 px-4 py-2"
            >
              {t('export.cancel')}
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || !canDownload}
              className="px-4 py-2 bg-[#0055FF] text-white text-sm font-medium hover:bg-[#0044CC] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('export.exporting')}</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>{t('export.download')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};
